import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { DataService } from '../services/data.service';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { GameState } from '../interfaces/game.interface';
import { NavService } from 'src/app/services/nav.service';

@Component({
  selector: 'app-game',
  templateUrl: './game.component.html',
  styleUrls: ['./game.component.scss']
})

export class GameComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(
    private dataService: DataService,
    private nav: NavService
  ) {}

  currentPage: string;
  latestQ1FeedbackPage: string;
  currentInfoDistrict: string;
  currentDistrict: number;
  currentShop: string;
  q1CorrectSelections: number;
  q1IncorrectShops: {id: string, feedback: string}[];

  sidePanelData$: Observable<{
    time: number,
    mcc: number,
    tposm_p1: number,
    tposm_p4: number,
    tposm_p5: number,
    tposm_full: number,
    incentives: number,
    disabled: boolean,
    quarter: number
  }>;

  private resizeObserver: ResizeObserver;
  private size = 20;

  ngOnInit(): void {
    this.dataService.initialize();
    this.initializeVars();
    this.initializeSidePanel();
  }

  ngAfterViewInit() {
    const container = document.querySelector('.game-wrap') as HTMLElement;

    this.resizeObserver = new ResizeObserver(() => {
      const containerWidth = container!.clientWidth;
      const containerHeight = container!.clientHeight;

      let height: number, width: number;
      if (containerWidth / containerHeight > 16 / 9) {
        height = containerHeight;
        width = containerHeight / 9 * 16;
      } else {
        width = containerWidth;
        height = containerWidth / 16 * 9;
      }

      const fontSize = width / 1920 * this.size;
      
      container.style.setProperty('--width', width.toString() + 'px');
      container.style.setProperty('--height', height.toString() + 'px');
      container.style.setProperty('--font-size', fontSize.toString() + 'px');
    });

    this.resizeObserver.observe(container!);
  }

  private initializeVars() {
    const game_state = this.dataService.getGameStateSnapshot();

    if (!game_state) {
      return setTimeout(() => {
        this.initializeVars();
      }, 500)
    }

    this.latestQ1FeedbackPage = game_state.latestQ1FeedbackPage;
    this.currentInfoDistrict = game_state.currentInfoDistrict;
    this.currentDistrict = game_state.currentDistrict;
    this.currentShop = game_state.currentShop;
    this.q1CorrectSelections = game_state.q1CorrectSelections;
    this.q1IncorrectShops = game_state.q1IncorrectShops;
    this.currentPage = game_state.currentPage;
  }

  patchState(patch: Partial<GameState>) {
    Object.assign(this, patch);
    this.dataService.updateState(patch);
  }

  private initializeSidePanel() {
    const game_state = this.dataService.getGameStateSnapshot();
    
    if (!game_state) {
      return setTimeout(() => {
        this.initializeSidePanel();
      }, 500)
    }

    this.sidePanelData$ = this.dataService.gameState$.pipe(
      filter((state): state is GameState => !!state),
      switchMap(state => {
        const quarterNumber = parseInt(state.game_state.replace('q', ''), 10);
        
        if (state.game_state === 'q1') {
          return of({
            time: 0,
            mcc: 0,
            tposm_p1: 0,
            tposm_p4: 0,
            tposm_p5: 0,
            tposm_full: 0,
            incentives: 0,
            disabled: true,
            quarter: quarterNumber
          });
        }
        
        return this.dataService.getResourceLimitsLeft().pipe(
          map(resources => ({
            time: resources.time,
            mcc: resources.mcc,
            tposm_p1: resources.tposm_p1,
            tposm_p4: resources.tposm_p4,
            tposm_p5: resources.tposm_p5,
            tposm_full: resources.tposm_full,
            incentives: resources.incentives,
            disabled: false,
            quarter: quarterNumber
          }))
        );
      })
    );
  }

  navigateToDistrictInfo(district: string) {
    this.patchState({
      currentInfoDistrict: district,
      currentPage: 'district-info'
    });
  }

  navigateToDistrict(district: number) {
    this.patchState({
      currentDistrict: district,
      currentPage: 'district'
    });
  }

  endQuarterOne( data: {
    success: boolean,
    correctSelections: number,
    incorrectIds: string[]
  }): void {
    const currentState = this.dataService.getGameStateSnapshot();

    if (data.success && data.incorrectIds.length === 0) {
      this.patchState({ 
        latestQ1FeedbackPage: 'q1-feedback-all-correct',
        currentPage: 'q1-feedback-all-correct'
      });
    } else {
      let localQ1IncorrectShops = data.incorrectIds.map((id: string) => ({
        id,
        feedback: currentState?.q1_stores?.[id]?.feedback || 'This is a wrong shop.'
      }));

      if (data.success) {
        this.patchState({
          q1CorrectSelections: data.correctSelections,
          q1IncorrectShops: localQ1IncorrectShops,
          latestQ1FeedbackPage: 'q1-feedback-enough-correct',
          currentPage: 'q1-feedback-enough-correct'
        });
      } else {
        this.patchState({
          q1CorrectSelections: data.correctSelections,
          q1IncorrectShops: localQ1IncorrectShops,
          latestQ1FeedbackPage: 'q1-feedback-some-correct',
          currentPage: 'q1-feedback-some-correct'
        });
      }
    } 
  }

  q1NavigateToSlider(shopId: string) {
    this.patchState({ 
      currentShop: shopId,
      currentPage: 'call-file-single' 
    });
  }

  onCallFileCloseClick(shopId: string) {
    this.patchState({ 
      currentShop: shopId,
      currentPage: 'taerra' 
    });
  }

  onNavigateToShop(shopId: string) {
    this.patchState({ 
      currentShop: shopId,
      currentPage: 'shop' 
    });
  }

  toString(i: number) {
    return String(i);
  }

  onChangeCurrentDistrict(i: number) {
    this.patchState({ currentDistrict: i });
  }

  endSimulation() {
    this.nav.navigate(['home-multi']);
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }
}
